import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e", goldL:"#d4a853",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  orange:"#d97706", orangeL:"rgba(217,119,6,0.10)",
  purple:"#7c3aed", purpleL:"rgba(124,58,237,0.10)",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

export function WinBackEngine() {
  const [clients, setClients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("hot");
  // treatmentFocus narrows every metric to a specific treatment family
  // (e.g. "Botox"). "All" preserves the original cross-treatment behavior.
  const [treatmentFocus, setTreatmentFocus] = useState("All");
  const [selected, setSelected] = useState(new Set());
  const [showModal, setShowModal] = useState(false);
  const [offer, setOffer] = useState("25");
  const [customOffer, setCustomOffer] = useState("");
  // "percent" → "20% off"
  // "dollar"  → "$25 off your visit"
  // "per_unit"→ "$10 per Botox unit" (Botox-only — per-unit pricing model)
  const [offerType, setOfferType] = useState("percent");
  const [tone, setTone] = useState("friendly");
  const [generating, setGenerating] = useState(false);
  const [generatedMessages, setGeneratedMessages] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [sending, setSending] = useState(false);
  const [campaignResult, setCampaignResult] = useState(null);
  const [step, setStep] = useState(1);
  
  useEffect(() => {
    const load = async () => {
      const fetchAll = async (table) => {
        const all = [];
        let from = 0;
        const PAGE = 1000;
        while (true) {
          const { data } = await supabase.from(table).select("*").range(from, from + PAGE - 1);
          if (!data || data.length === 0) break;
          all.push(...data);
          if (data.length < PAGE) break;
          from += PAGE;
        }
        return all;
      };
      const [c, a] = await Promise.all([fetchAll("clients"), fetchAll("appointments")]);
      setClients(c);
      setAppointments(a);
      setLoading(false);
    };
    load();
  }, []);
  
  // Surface every distinct treatment name that's ever been booked, so the
  // owner can pivot the segmentation to Botox / Filler / Laser / whatever
  // without us hard-coding a list. Sorted alphabetically.
  const treatmentOptions = (() => {
    const seen = new Set();
    for (const a of appointments) {
      const t = (a.treat || "").trim();
      if (t) seen.add(t);
    }
    return ["All", ...Array.from(seen).sort()];
  })();

  // Match against treatment family by case-insensitive substring (so
  // "Botox" matches "Botox - Glabella", "Botox Touchup", etc.). This keeps
  // the filter forgiving when service names have variants in the DB.
  const matchesTreatment = (treatStr) => {
    if (treatmentFocus === "All") return true;
    if (!treatStr) return false;
    return treatStr.toLowerCase().includes(treatmentFocus.toLowerCase());
  };

  const enriched = clients.map(c => {
    const clientAppts = appointments.filter(a => a.cid === c.id);
    // Treatment-scoped slice — used when the focus filter is active.
    const focusedAppts = clientAppts.filter(a =>
      matchesTreatment(a.treat) && a.status !== "cancelled" && a.status !== "no_show" && a.status !== "no-show"
    );

    // When focused on a specific treatment, the dates/visit-count switch to
    // that treatment's history. When "All", we use the original cross-
    // treatment behavior so existing campaigns keep working as before.
    const sortedAll = [...clientAppts].sort((x, y) => (y.date || "").localeCompare(x.date || ""));
    const sortedFocused = [...focusedAppts].sort((x, y) => (y.date || "").localeCompare(x.date || ""));

    const lastDateFromAppts = (treatmentFocus !== "All" ? sortedFocused : sortedAll)[0]?.date;
    const lastDateFromCSV = treatmentFocus === "All" ? c.last : null;
    const lastDate = lastDateFromAppts || lastDateFromCSV;
    const lastTreatment = sortedAll.find(a => a.status === "completed")?.treat || sortedAll[0]?.treat || (c.member_plan && c.member_plan !== "None" ? c.member_plan : null);
    const daysSince = lastDate ? Math.floor((new Date() - new Date(lastDate)) / 86400000) : 9999;
    const spent = parseFloat(c.spent) || 0;
    const apptVisits = treatmentFocus !== "All"
      ? focusedAppts.length
      : clientAppts.filter(a => a.status === "completed").length;
    const estimatedVisits = treatmentFocus === "All" && spent > 0 ? Math.max(1, Math.round(spent / 300)) : 0;
    const visits = apptVisits || estimatedVisits;

    let score = 0;
    if (spent >= 5000) score += 40;
    else if (spent >= 2000) score += 30;
    else if (spent >= 500) score += 20;
    else if (spent >= 100) score += 10;
    if (daysSince <= 180) score += 30;
    else if (daysSince <= 365) score += 20;
    else if (daysSince <= 730) score += 10;
    if (visits >= 5) score += 20;
    else if (visits >= 3) score += 15;
    else if (visits >= 1) score += 10;
    if (c.email && c.phone) score += 10;

    // Segments change meaning when a specific treatment is focused.
    // Treatment view (Botox etc.):
    //   hot  = 3–5 mo since last visit → due for a refresh, prime target
    //   cold = 5–12 mo → likely drifting, needs incentive
    //   vip  = 3+ visits of this treatment AND ≤ 6 mo → loyal regulars
    //   lost = 12+ mo since last visit of this treatment
    // The "All" mode keeps the original revenue-tier segmentation.
    let segment = "lost";
    if (treatmentFocus !== "All") {
      if (visits >= 3 && daysSince <= 180) segment = "vip";
      else if (daysSince >= 85 && daysSince <= 150) segment = "hot";
      else if (daysSince > 150 && daysSince <= 365) segment = "cold";
      else segment = "lost";
    } else {
      if (spent >= 1000 && daysSince >= 90 && daysSince <= 180) segment = "hot";
      else if (spent >= 500 && daysSince > 180 && daysSince <= 365) segment = "cold";
      else if (spent >= 5000 && daysSince >= 60) segment = "vip";
    }

    return { ...c, daysSince, spent, visits, score, segment, lastDate, lastTreatment };
  });

  // In treatment-focus mode we only look at clients who've actually had
  // the focused treatment. Everyone else is irrelevant for this campaign.
  const baseList = treatmentFocus === "All"
    ? enriched
    : enriched.filter(c => c.visits > 0);

  const atRiskAll = baseList.filter(c => c.daysSince >= 60 && (c.spent > 0 || c.visits > 0));
  const filtered = filter === "all" ? atRiskAll
    : filter === "hot" ? atRiskAll.filter(c => c.segment === "hot")
    : filter === "cold" ? atRiskAll.filter(c => c.segment === "cold")
    : filter === "vip" ? atRiskAll.filter(c => c.segment === "vip")
    : atRiskAll.filter(c => c.segment === "lost");
  filtered.sort((a, b) => b.score - a.score);
  
  const potentialRevenue = atRiskAll.reduce((s, c) => s + Math.min(c.spent * 0.3, 500), 0);
  // Counters scope to baseList so the chip totals reflect the active
  // treatment filter. In "All" mode this is identical to enriched.
  const hotCount = baseList.filter(c => c.segment === "hot").length;
  const coldCount = baseList.filter(c => c.segment === "cold").length;
  const vipCount = baseList.filter(c => c.segment === "vip").length;
  const lostCount = baseList.filter(c => c.segment === "lost").length;
  
  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const selectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(c => c.id)));
  };
  
  // Generate AI personalized messages
  const generateMessages = async () => {
    setGenerating(true);
    const targets = filtered.filter(c => selected.has(c.id));
    const messages = {};
    const offerValue = customOffer || offer;
    
    for (const client of targets) {
      try {
        const firstName = client.name?.split(" ")[0] || "there";
        const monthsSince = Math.round(client.daysSince / 30);
        const treatmentHint = client.lastTreatment ? `Last treatment: ${client.lastTreatment}.` : "";
        
        const toneInstruction = tone === "friendly" 
          ? "Warm, casual, like a friend reaching out. Use 1-2 emojis."
          : tone === "professional"
          ? "Professional, respectful, elegant. No emojis or 1 subtle emoji."
          : "Exclusive, VIP feel. Make them feel special. Premium tone with 1-2 elegant emojis.";
        
        const prompt = `Write a personalized SMS (MAX 160 characters) to win back ${firstName} from Nova Med Spa.

Client info:
- First name: ${firstName}
- Last visit: ${monthsSince} months ago (${client.daysSince} days)
- Lifetime spent: $${Math.round(client.spent)}
- ${treatmentHint}
- Total visits: ${client.visits}

Offer: ${offerValue}% off their next treatment.

Tone: ${toneInstruction}

Rules:
- Use ${firstName} by name
- Mention how long it's been (in natural way)
- Reference what they had before if relevant
- Include the discount
- End with: novamedicalspa.com
- MAX 160 chars
- Just the SMS text, no explanations`;

        const { data, error } = await supabase.functions.invoke("generate-winback", {
          body: {
            client: {
              name: client.name,
              daysSince: client.daysSince,
              spent: client.spent,
              visits: client.visits,
              lastTreatment: client.lastTreatment,
              segment: client.segment,           // "hot" | "cold" | "vip" | "lost"
            },
            offer: offerValue,
            offerType,                            // "percent" | "dollar"
            treatmentFocus,                       // "Botox" / "Filler" / "All"
            tone: tone
          }
        });

        // Friendly fallback strings respect offer type too
        const offerStr = offerType === "per_unit" ? `$${offerValue}/unit ${treatmentFocus !== "All" ? treatmentFocus : ""} special`
                       : offerType === "dollar"   ? `$${offerValue} off`
                       :                            `${offerValue}% off`;
        if (data?.message && data?.success) {
          messages[client.id] = data.message;
        } else {
          console.warn("AI failed for", client.name, error || data?.error);
          messages[client.id] = `Hi ${firstName}! It's been ${monthsSince} months. We miss you at Nova Med Spa! ${offerStr} your next visit 💕 novamedicalspa.com`;
        }
      } catch (e) {
        const firstName = client.name?.split(" ")[0] || "there";
        const offerStr = offerType === "per_unit" ? `$${offerValue}/unit ${treatmentFocus !== "All" ? treatmentFocus : ""} special`
                       : offerType === "dollar"   ? `$${offerValue} off`
                       :                            `${offerValue}% off`;
        messages[client.id] = `Hi ${firstName}! Take ${offerStr} your next treatment at Nova Med Spa 💕 novamedicalspa.com`;
      }
      
      setGeneratedMessages({...messages});
      await new Promise(r => setTimeout(r, 300));
    }
    
    setGenerating(false);
    setStep(2);
  };
  
  const sendCampaign = async () => {
    setSending(true);
    const results = { sent: 0, failed: 0, errors: [] };
    const targets = filtered.filter(c => selected.has(c.id));
    
    for (const client of targets) {
      const message = generatedMessages[client.id];
      if (!message) { results.failed++; continue; }
      
      try {
        if (client.phone) {
          const { error } = await supabase.functions.invoke("send-sms", {
            // sender:"ai" + source:"winback_campaign" so the AI receptionist
            // sees this message in conversation history when the client replies,
            // and can pick the thread up naturally without re-asking what
            // treatment they're interested in.
            body: { to: client.phone, message, sender: "ai", source: "winback_campaign" }
          });
          if (error) throw error;
          results.sent++;
        } else {
          results.failed++;
          results.errors.push(`${client.name}: no phone`);
        }
      } catch (err) {
        results.failed++;
        results.errors.push(`${client.name}: ${err.message}`);
      }
      await new Promise(r => setTimeout(r, 200));
    }
    
    setCampaignResult(results);
    setSending(false);
    setShowModal(false);
    setStep(1);
    setGeneratedMessages({});
    setSelected(new Set());
    
    try {
      await supabase.from("activity_log").insert([{
        action: "winback_campaign",
        details: { sent: results.sent, failed: results.failed, segment: filter, offer: customOffer || offer, tone }
      }]);
    } catch (e) {}
  };
  
  if (loading) return <div style={{padding:40,textAlign:"center",color:C.muted}}>Loading win-back data...</div>;
  
  const targetClients = filtered.filter(c => selected.has(c.id));
  
  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div>
        <div style={{fontSize:22,fontWeight:800,color:C.gold,fontFamily:S}}>🔥 Win-Back Engine</div>
        <div style={{fontSize:12,color:C.muted,marginTop:2}}>AI-personalized messages to reactivate at-risk clients</div>
      </div>
      
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12}}>
        <KPI label="Potential Revenue" value={"$"+Math.round(potentialRevenue).toLocaleString()} color={C.green} icon="💰" sub="If 30% return"/>
        <KPI label="At-Risk Total" value={atRiskAll.length} color={C.red} icon="⚠️"/>
        <KPI label="Hot Leads" value={hotCount} color={C.orange} icon="🔥" sub="90-180 days"/>
        <KPI label="VIP Win-Back" value={vipCount} color={C.purple} icon="⭐" sub="$5K+ inactive"/>
      </div>
      
      {campaignResult && (
        <div style={{padding:16,background:C.greenL,border:"1px solid "+C.green,borderRadius:11}}>
          <div style={{fontSize:14,fontWeight:700,color:C.green,marginBottom:6}}>✅ Campaign Sent!</div>
          <div style={{fontSize:12,color:C.text}}>Sent: <strong>{campaignResult.sent}</strong> · Failed: <strong>{campaignResult.failed}</strong></div>
          <button onClick={()=>setCampaignResult(null)} style={{marginTop:8,padding:"5px 12px",background:"#fff",color:C.green,border:"1px solid "+C.green,borderRadius:6,cursor:"pointer",fontSize:11,fontWeight:600}}>Dismiss</button>
        </div>
      )}
      
      <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap",padding:"10px 14px",background:treatmentFocus==="All"?"transparent":C.goldBg,border:"1px solid "+(treatmentFocus==="All"?C.border:C.borderGold),borderRadius:10}}>
        <label style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:0.5}}>Treatment focus</label>
        <select
          value={treatmentFocus}
          onChange={(e)=>{ setTreatmentFocus(e.target.value); setSelected(new Set()); }}
          style={{padding:"8px 12px",background:"#fff",border:"1px solid "+C.border,borderRadius:8,fontSize:14,fontFamily:F,minWidth:180,cursor:"pointer"}}
        >
          {treatmentOptions.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {treatmentFocus !== "All" && (
          <div style={{fontSize:11,color:C.muted,flex:1,minWidth:140}}>
            Segments rescoped to clients who had <strong>{treatmentFocus}</strong>.
            Hot = 3-5 mo since last, due for refresh.
          </div>
        )}
      </div>

      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
        {[
          ["vip", treatmentFocus==="All"?"⭐ VIP":"⭐ Regulars",        vipCount,  C.purple],
          ["hot", treatmentFocus==="All"?"🔥 Hot":"🔥 Due Now (3-5mo)", hotCount,  C.orange],
          ["cold",treatmentFocus==="All"?"🟡 Cold":"🟡 Drifting",        coldCount, C.blue],
          ["lost",treatmentFocus==="All"?"⚫ Lost":"⚫ 12mo+",            lostCount, C.muted],
          ["all","📋 All",atRiskAll.length,C.gold],
        ].map(([key,label,count,color])=>(
          <button key={key} onClick={()=>{setFilter(key);setSelected(new Set());}} style={{padding:"8px 14px",fontSize:12,fontWeight:filter===key?700:500,color:filter===key?"#fff":color,background:filter===key?color:"transparent",border:"1.5px solid "+color,borderRadius:8,cursor:"pointer",fontFamily:F,whiteSpace:"nowrap"}}>{label} ({count})</button>
        ))}
      </div>
      
      <div style={{display:"flex",gap:10,alignItems:"center",padding:"12px 16px",background:C.goldBg,border:"1px solid "+C.borderGold,borderRadius:11,flexWrap:"wrap"}}>
        <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={selectAll} style={{cursor:"pointer",width:16,height:16}}/>
        <div style={{fontSize:13,fontWeight:600,color:C.text}}>{selected.size} selected of {filtered.length}</div>
        <div style={{flex:1}}/>
        <button disabled={selected.size === 0} onClick={()=>setShowModal(true)} style={{padding:"9px 18px",background:selected.size>0?C.gold:C.dim,color:"#fff",border:"none",borderRadius:8,cursor:selected.size>0?"pointer":"not-allowed",fontSize:13,fontWeight:700,fontFamily:F}}>🤖 Generate AI Messages ({selected.size})</button>
      </div>
      
      <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,overflow:"hidden",maxHeight:600,overflowY:"auto"}}>
        {filtered.length === 0 ? (
          <div style={{padding:40,textAlign:"center",color:C.dim}}>No clients in this segment 🎉</div>
        ) : filtered.slice(0, 200).map(c => (
          <div key={c.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",borderBottom:"1px solid "+C.border,background:selected.has(c.id)?C.goldBg:"transparent",cursor:"pointer"}} onClick={()=>toggleSelect(c.id)}>
            <input type="checkbox" checked={selected.has(c.id)} onChange={()=>{}} style={{cursor:"pointer",width:16,height:16,flexShrink:0}}/>
            <div style={{width:36,height:36,borderRadius:9,background:c.score>=70?C.greenL:c.score>=40?C.orangeL:C.redL,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:c.score>=70?C.green:c.score>=40?C.orange:C.red,flexShrink:0}}>{c.score}</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</div>
              <div style={{fontSize:11,color:C.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                💰 ${Math.round(c.spent).toLocaleString()} · 📅 {c.daysSince} days · {c.lastTreatment || "No treatments"}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* MEGA MODAL: Step 1 = Settings, Step 2 = Preview & Edit */}
      {showModal && (
        <div onClick={()=>{if(!generating&&!sending)setShowModal(false);}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:10000,padding:20}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:14,padding:24,maxWidth:700,width:"100%",maxHeight:"92vh",overflowY:"auto"}}>
            
            {step === 1 && (
              <>
                <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:14}}>🤖 Generate AI Messages</div>
                <div style={{padding:12,background:C.goldBg,borderRadius:8,marginBottom:14}}>
                  <div style={{fontSize:13,color:C.text}}>AI will create <strong>{selected.size} unique messages</strong></div>
                  <div style={{fontSize:11,color:C.muted,marginTop:2}}>Each personalized with name, treatment history, and time since last visit</div>
                </div>
                
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:6,textTransform:"uppercase"}}>Offer Type</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginBottom:10}}>
                    {[
                      ["percent","% Discount","20%, 25% off"],
                      ["dollar","$ Off Visit","$10, $25 off"],
                      ["per_unit","$/Unit Special","$10/unit Botox"],
                    ].map(([key,label,desc])=>(
                      <button key={key} onClick={()=>{setOfferType(key); setCustomOffer(""); setOffer(key==="percent"?"25":"10");}} style={{padding:"10px 10px",background:offerType===key?C.gold:"#fff",color:offerType===key?"#fff":C.text,border:"1.5px solid "+(offerType===key?C.gold:C.border),borderRadius:8,cursor:"pointer",fontSize:11,fontWeight:600,textAlign:"left"}}>
                        <div style={{fontWeight:700}}>{label}</div>
                        <div style={{fontSize:10,opacity:0.8,marginTop:2}}>{desc}</div>
                      </button>
                    ))}
                  </div>
                  <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:6,textTransform:"uppercase"}}>
                    {offerType==="per_unit"?"Price Per Unit":offerType==="dollar"?"Dollar Off":"Discount Percentage"}
                  </div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {(offerType==="per_unit" ? ["9","10","11","12"]
                      : offerType==="dollar" ? ["10","25","50","100"]
                      : ["20","25","30","50"]).map(p=>(
                      <button key={p} onClick={()=>{setOffer(p);setCustomOffer("");}} style={{padding:"8px 14px",background:offer===p&&!customOffer?C.gold:"#fff",color:offer===p&&!customOffer?"#fff":C.text,border:"1.5px solid "+(offer===p&&!customOffer?C.gold:C.border),borderRadius:7,cursor:"pointer",fontSize:13,fontWeight:600}}>
                        {offerType==="per_unit"?`$${p}/unit`:offerType==="dollar"?`$${p} off`:`${p}% off`}
                      </button>
                    ))}
                    <input value={customOffer} onChange={e=>setCustomOffer(e.target.value)} placeholder={offerType==="per_unit"?"$/unit":offerType==="dollar"?"$ Custom":"% Custom"} style={{padding:"8px 12px",border:"1.5px solid "+C.border,borderRadius:7,fontSize:13,width:90}}/>
                  </div>
                  {offerType==="per_unit" && (
                    <div style={{marginTop:8,padding:"8px 10px",background:C.orangeL,border:"1px solid "+C.orange+"40",borderRadius:6,fontSize:11,color:C.orange,fontWeight:600}}>
                      💡 Per-unit pricing is best for Botox / Dysport. Make sure Treatment Focus is set to "Botox" above so the message makes sense.
                    </div>
                  )}
                </div>
                
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:6,textTransform:"uppercase"}}>Message Tone</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6}}>
                    {[
                      ["friendly","💕 Friendly","Warm, casual"],
                      ["professional","💼 Professional","Elegant, refined"],
                      ["vip","⭐ VIP","Exclusive, premium"]
                    ].map(([key,label,desc])=>(
                      <button key={key} onClick={()=>setTone(key)} style={{padding:"10px 8px",background:tone===key?C.gold:"#fff",color:tone===key?"#fff":C.text,border:"1.5px solid "+(tone===key?C.gold:C.border),borderRadius:8,cursor:"pointer",fontSize:11,fontWeight:600}}>
                        <div>{label}</div>
                        <div style={{fontSize:9,opacity:0.8,marginTop:2}}>{desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div style={{display:"flex",gap:8,marginTop:18}}>
                  <button onClick={()=>setShowModal(false)} disabled={generating} style={{flex:1,padding:11,background:"#fff",color:C.muted,border:"1.5px solid "+C.border,borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:600}}>Cancel</button>
                  <button disabled={generating} onClick={generateMessages} style={{flex:2,padding:11,background:generating?C.dim:C.gold,color:"#fff",border:"none",borderRadius:8,cursor:generating?"wait":"pointer",fontSize:13,fontWeight:700}}>{generating?`Generating ${Object.keys(generatedMessages).length}/${selected.size}...`:`🤖 Generate ${selected.size} Messages`}</button>
                </div>
              </>
            )}
            
            {step === 2 && (
              <>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                  <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S}}>✏️ Preview & Edit</div>
                  <button onClick={()=>setStep(1)} style={{padding:"5px 10px",background:"#fff",color:C.muted,border:"1px solid "+C.border,borderRadius:6,cursor:"pointer",fontSize:11}}>← Back</button>
                </div>
                
                <div style={{padding:12,background:C.greenL,borderRadius:8,marginBottom:14}}>
                  <div style={{fontSize:13,color:C.text}}>✅ <strong>{Object.keys(generatedMessages).length} messages ready</strong></div>
                  <div style={{fontSize:11,color:C.muted,marginTop:2}}>Click any message to edit it manually</div>
                </div>
                
                <div style={{maxHeight:400,overflowY:"auto",marginBottom:14}}>
                  {targetClients.map(client => (
                    <div key={client.id} style={{padding:"10px 12px",borderBottom:"1px solid "+C.border}}>
                      <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:4}}>
                        {client.name} <span style={{color:C.muted,fontWeight:500}}>· {client.phone}</span>
                      </div>
                      {editingId === client.id ? (
                        <textarea autoFocus value={generatedMessages[client.id] || ""} onChange={e=>setGeneratedMessages({...generatedMessages, [client.id]: e.target.value})} onBlur={()=>setEditingId(null)} maxLength={160} rows={3} style={{width:"100%",padding:8,border:"1.5px solid "+C.gold,borderRadius:6,fontSize:11,fontFamily:F,resize:"vertical"}}/>
                      ) : (
                        <div onClick={()=>setEditingId(client.id)} style={{padding:"8px 10px",background:C.light,borderRadius:6,fontSize:11,color:C.text,cursor:"text",fontFamily:F,lineHeight:1.4}}>
                          {generatedMessages[client.id] || <em style={{color:C.dim}}>Generating...</em>}
                          <span style={{display:"block",marginTop:4,fontSize:9,color:C.muted}}>{(generatedMessages[client.id] || "").length}/160 chars</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>{setShowModal(false);setStep(1);setGeneratedMessages({});}} disabled={sending} style={{flex:1,padding:11,background:"#fff",color:C.muted,border:"1.5px solid "+C.border,borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:600}}>Cancel</button>
                  <button disabled={sending || Object.keys(generatedMessages).length === 0} onClick={sendCampaign} style={{flex:2,padding:11,background:sending?C.dim:C.green,color:"#fff",border:"none",borderRadius:8,cursor:sending?"wait":"pointer",fontSize:13,fontWeight:700}}>{sending?"Sending...":`🚀 Send ${Object.keys(generatedMessages).length} SMS Now`}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({label,value,color,icon,sub}) {
  return (
    <div style={{padding:"16px 18px",background:"#fff",border:"1px solid "+C.border,borderRadius:11}}>
      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
        <span style={{fontSize:18}}>{icon}</span>
        <span style={{fontSize:11,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>{label}</span>
      </div>
      <div style={{fontSize:24,fontWeight:800,color:color,fontFamily:S}}>{value}</div>
      {sub&&<div style={{fontSize:10,color:C.dim,marginTop:2}}>{sub}</div>}
    </div>
  );
}
